import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import AppContext from "../../../../context/AppContext";
import { getEmailData, getJobAddress } from "../../../../services/EmailService";
import { getJobOrders } from "../../../../services/OrderService";

import JobOrdersTable from "../../../../components/CustomTable/JobOrdersTable";

const useStyles = makeStyles({
  root: {},
  navLink: {
    textDecoration: "none",
  },
});

const websiteURL = "https://horizoncarpentry.com";

export default function OrderPreview() {
  const classes = useStyles();
  const { user } = useContext(AppContext);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const jobId = params.get("jid"); //JOB_ID
  const supplierId = params.get("sid"); //SUPPLIER_ID
  const unit = params.get("unit"); //UNIT_ID
  const [emailData, setEmailData] = useState(null);
  const [jobOrders, setJobOrders] = useState([]);
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [supplierData, setSupplierData] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingEmailData, setLoadingEmailData] = useState(false);

  const retrieveEmailData = useCallback(async (JID, SID) => {
    try {
      setLoadingEmailData(true);
      getEmailData(JID, SID).then((res) => {
        const newEmailData = res[0];

        setLoadingEmailData(false);
        setEmailData(newEmailData);
      });
    } catch (e) {
      console.error(e);
      setLoadingEmailData(false);
      toast.error("Unexpected error occurs while loading the email data!");
    }
  }, []);

  const retrieveJobAddress = useCallback(async (JID, UNIT) => {
    try {
      setLoadingEmailData(true);
      getJobAddress(JID, UNIT).then((res) => {
        const newJobAddressData = res[0];

        setLoadingEmailData(false);
        setEmailData(newJobAddressData);
      });
    } catch (e) {
      console.error(e);
      setLoadingEmailData(false);
    }
  }, []);

  const retrieveJobOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      getJobOrders(jobId).then((res) => {
        setLoadingOrders(false);
        setJobOrders(res);
      });
    } catch (e) {
      console.error(e);
      setLoadingOrders(false);
      toast.error("Unexpected error occurs while loading the orders!");
    }
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    if (supplierId > 0) {
      retrieveEmailData(jobId, supplierId);
    } else retrieveJobAddress(jobId, unit);

    retrieveJobOrders(jobId);
  }, [jobId, supplierId, unit]);

  useEffect(() => {
    const newSupplierOrders = jobOrders
      .filter((item) => parseInt(item.UNIT_ID) === parseInt(unit))
      .flatMap((item) => {
        const materialsItems = JSON.parse(item.ORDER_ITEMS);

        return materialsItems && materialsItems.length > 0
          ? [
              {
                ...item,
                SUPPLIER_ID: materialsItems[0].SUPPLIER_ID,
                SUPPLIER_NAME: materialsItems[0].SUPPLIER_NAME,
              },
            ]
          : [{ ...item }];
      })
      .filter(
        (item) => !supplierId || item.SUPPLIER_ID === parseInt(supplierId)
      );

    const newSupplierData = newSupplierOrders.find(
      (item) => supplierId && item.SUPPLIER_ID === parseInt(supplierId)
    );

    setSupplierOrders(newSupplierOrders);
    setSupplierData(newSupplierData || null);
  }, [supplierId, unit, jobOrders]);

  return (
    <Container component="main">
      <Box sx={{ mb: 10, mt: 10 }}>
        <Card>
          <CardHeader
            title={
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item>
                  <a
                    className={classes.navLink}
                    href={websiteURL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Typography variant="h5" color="white">
                      HORIZON CARPENTRY
                    </Typography>
                  </a>
                </Grid>
                <Grid item>
                  <Typography variant="h6" color="white">
                    ORDER SUMMARY
                  </Typography>
                </Grid>
              </Grid>
            }
            sx={{ backgroundColor: "orange" }}
          />
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Grid container justifyContent="space-between" spacing={4}>
                  <Grid item>
                    <Typography variant="body2">PO BOX 5874</Typography>
                    <Typography variant="body2">
                      Elgin, Illnois 60121
                    </Typography>
                    <Typography variant="body2">
                      <strong>Office:</strong> 815-572-5656
                    </Typography>
                    <Typography variant="body2">
                      <strong>Fax:</strong> 815-572-5011
                    </Typography>
                  </Grid>
                  <Grid item>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Supplier #</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Name</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              {loadingOrders ? (
                                <Skeleton variant="text" width="50px" />
                              ) : (
                                supplierData?.SUPPLIER_ID || supplierId
                              )}
                            </TableCell>
                            <TableCell>
                              {loadingOrders ? (
                                <Skeleton variant="text" width="200px" />
                              ) : (
                                supplierData?.SUPPLIER_NAME ||
                                (parseInt(supplierId) === 0 ? "Horizon" : "")
                              )}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardHeader
                        title={<Typography variant="h6">To:</Typography>}
                      />
                      <Divider />
                      <CardContent>
                        {loadingEmailData ? (
                          <>
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                          </>
                        ) : (
                          <>
                            {emailData?.NAME && (
                              <Typography variant="body2">
                                {emailData?.NAME}
                              </Typography>
                            )}
                            {emailData?.CONTACT && (
                              <Typography variant="body2">
                                {emailData?.CONTACT}
                              </Typography>
                            )}
                            {emailData?.ADDRESS && (
                              <Typography variant="body2">
                                {emailData?.ADDRESS}
                              </Typography>
                            )}
                            {emailData?.PHONE && (
                              <Typography variant="body2">
                                <strong>Phone: </strong> {emailData?.PHONE}
                              </Typography>
                            )}
                            {emailData?.FAX && (
                              <Typography variant="body2">
                                <strong>Fax: </strong> {emailData?.FAX}
                              </Typography>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardHeader
                        title={<Typography variant="h6">Ship To:</Typography>}
                      />
                      <Divider />
                      <CardContent>
                        {loadingEmailData ? (
                          <>
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="50%" />
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="50%" />
                          </>
                        ) : (
                          <>
                            <Typography variant="body2">
                              <strong>Project:</strong> {emailData?.DESCRIPTION}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Bld/Lot:</strong> {emailData?.JOB_DESC}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Job Id:</strong> {jobId}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Address/Unit:</strong>{" "}
                              {emailData?.DESCRIPTION1}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Builder:</strong> {emailData?.BUILDER}
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={4}>
                  <Grid item xs={6}></Grid>
                  <Grid item xs={6}>
                    {loadingEmailData ? (
                      <>
                        <Skeleton variant="text" width="80px" />
                        <Skeleton variant="text" width="80px" />
                        <Skeleton variant="text" width="50px" />
                      </>
                    ) : (
                      <>
                        <Typography variant="body2">
                          <strong>Model: </strong>
                          {emailData?.MODEL}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Elevation: </strong>
                          {emailData?.ELEVATION}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Type: </strong>
                          {emailData?.SHORT_DESC}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <JobOrdersTable
                  title="Orders"
                  loading={loadingOrders}
                  items={supplierOrders}
                  hiddenColumns={
                    user?.USER_ROLE === "FOREMAN" ? ["PO_TOTAL"] : []
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
